import type { Role } from '@natashu/shared';
import { formatRelativeThai } from '@natashu/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';

/** GET /api/presence (apps/server/src/modules/presence/routes.ts). */
export interface PresenceItem {
  id: string;
  displayName: string;
  role: Role;
  roleLabelTh: string;
  online: boolean;
  lastSeenAt: string | null;
  isSelf: boolean;
}

export interface PresenceList {
  windowSeconds: number;
  serverTime: string;
  onlineCount: number;
  items: PresenceItem[];
}

/** Invalidated by the realtime `presence.updated` event (lib/realtime.ts). */
export const presenceKey = ['presence'] as const;

export function usePresenceList() {
  const { status } = useAuth();
  return useQuery({
    queryKey: presenceKey,
    queryFn: ({ signal }) => api.get<PresenceList>('/api/presence', { signal }),
    enabled: status === 'authenticated',
    staleTime: 15_000,
    // Covers users who dropped off without an event (e.g. their API instance crashed).
    refetchInterval: 60_000,
  });
}

/** "ออนไลน์อยู่" / "ออนไลน์ล่าสุด 5 นาทีที่แล้ว" / "ออนไลน์ล่าสุดเมื่อวาน" / "ยังไม่เคยออนไลน์". */
export function presenceStatusText(item: Pick<PresenceItem, 'online' | 'lastSeenAt'>, now: Date = new Date()): string {
  if (item.online) return 'ออนไลน์อยู่';
  if (!item.lastSeenAt) return 'ยังไม่เคยออนไลน์';
  const rel = formatRelativeThai(item.lastSeenAt, now);
  if (rel === 'เมื่อสักครู่' || rel === 'เมื่อวาน') return `ออนไลน์ล่าสุด${rel}`;
  if (/(นาที|ชม\.)$/.test(rel)) return `ออนไลน์ล่าสุด ${rel}ที่แล้ว`;
  return `ออนไลน์ล่าสุด ${rel}`;
}
